import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Switch,
  IconButton,
  Box,
  HStack,
  VStack,
  Textarea,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import { BASE_URL } from "../../../Globalapi/Globalapi";

const FeesTypeModal = ({ initialData, isOpen, onClose, refreshData }) => {
  const [feeGroup, setFeeGroup] = useState("");
  const [description, setDescription] = useState("");
  const [fields, setFields] = useState([
    { type_name: "", type_id: null, is_deleted: false },
  ]); // added type_id and is_deleted
  const [status, setStatus] = useState(false);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    const fetchGroupDetails = async () => {
      if (!initialData) return;

      setLoading(true);

      try {
        const response = await fetch(
          `${BASE_URL}/admin/Fees/fees_group/singleview_fee_category.php`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              org_id: sessionStorage.getItem("cmp_id"),
              user_id: sessionStorage.getItem("user_id"),
              group_id: initialData,
            }),
          }
        );
        const result = await response.json();

        if (result.success) {
          const { group_name, description, fee_types, status } = result.data;
          setFeeGroup(group_name);
          setDescription(description);
          setFields(
            fee_types.map((fee) => ({
              ...fee,
              is_deleted: false, // Ensure new fee types are not marked as deleted
            }))
          );
          setStatus(status);
        } else {
          toast({
            title: "Error",
            description: result.message,
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "Unable to fetch group details. Please try again later.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchGroupDetails();
  }, [initialData, toast]);

  const handleFieldChange = (index, key, value) => {
    const updatedFields = [...fields];
    updatedFields[index][key] = value;
    setFields(updatedFields);
  };

  const addField = () => {
    setFields([...fields, { type_name: "", type_id: null, is_deleted: false }]); // Add new fee type with empty values
  };

  const removeField = (index) => {
    const updatedFields = [...fields];
    updatedFields[index].is_deleted = true; // Mark as deleted instead of removing directly
    setFields(updatedFields);
  };

  const handleSubmit = async () => {
    const payload = {
      group_name: feeGroup,
      description,
      org_id: sessionStorage.getItem("cmp_id"),
      user_id: sessionStorage.getItem("user_id"),
      fee_types: fields, // Include all fee types (including the deleted ones)
      status: status,
      group_id: initialData,
    };
    console.log(payload);

    const response = await fetch(
      `${BASE_URL}/admin/Fees/fees_group/edit_fee_group.php`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );
    const result = await response.json();
    console.log(result);

    if (result.success) {
      toast({
        title: "Success",
        description: result.message,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      onClose();
      refreshData();
    } else {
      // Show error toast if result doesn't indicate success
      toast({
        title: "Error",
        description: result.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Fees Group</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loading ? (
            <Spinner size="xl" />
          ) : (
            <VStack spacing={4} align="stretch" mb={4}>
              <FormControl>
                <FormLabel>Fee Group</FormLabel>
                <Input
                  placeholder="Enter Fee Group"
                  value={feeGroup}
                  onChange={(e) => setFeeGroup(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  placeholder="Enter Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </FormControl>
              <FormLabel>Fee Type</FormLabel>
              {fields
                .filter((field) => !field.is_deleted) // Exclude deleted fields
                .map((field, index) => (
                  <Box key={index} mb={4}>
                    <HStack spacing={4} align="start">
                      <FormControl>
                        <Input
                          placeholder="Enter Fee Type Name"
                          value={field.type_name}
                          onChange={(e) =>
                            handleFieldChange(
                              index,
                              "type_name",
                              e.target.value
                            )
                          }
                        />
                      </FormControl>
                      <IconButton
                        icon={<DeleteIcon />}
                        colorScheme="red"
                        size="sm"
                        aria-label="Remove Field"
                        onClick={() => removeField(index)}
                      />
                    </HStack>
                  </Box>
                ))}

              <Button
                colorScheme="blue"
                size="sm"
                leftIcon={<AddIcon />}
                onClick={addField}
              >
                Add Fee Type
              </Button>
              <FormControl mt={6}>
                <FormLabel>Status</FormLabel>
                <Switch
                  colorScheme="blue"
                  isChecked={status}
                  onChange={(e) => setStatus(e.target.checked)}
                />
              </FormControl>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} mr={3}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSubmit}>
            Save Fees Group
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default FeesTypeModal;
